import { FieldValues, UseFormSetError } from 'react-hook-form'
import i18next from 'i18next'
import { CountriesWithStates } from 'constants/Countries'
import { ApiErrorResponse } from 'types/errors'
import { FormFieldsType } from 'types/formField'
import { getRequestErrors } from 'utils/errors'
import { getStringWithoutPrefix } from 'utils/utils'
import { AddressErrors, AddressFormKeyList } from './constants'
import { AddressFields, AddressFormKey, GetAddressErrorMethod } from './types'

export const getAddressFields = <FieldKeys extends string>(
  formFields: FormFieldsType<FieldKeys>,
  keyPrefix?: string
): AddressFields => {
  return Object.keys(formFields)?.reduce((addressObj, fieldKey) => {
    const addressKey = getStringWithoutPrefix(fieldKey, keyPrefix)
    if (AddressFormKeyList?.includes(addressKey as AddressFormKey)) {
      addressObj[addressKey] = formFields?.[fieldKey]
    }
    return addressObj
  }, {} as AddressFields)
}

export const getAddressFieldVersion = (key: string, convertToV2?: boolean) => {
  const fieldPrefixV1 = 'address'
  const fieldPrefixV2 = 'addressLine'
  if (convertToV2) {
    return key?.startsWith(fieldPrefixV1) ? key?.replace(fieldPrefixV1, fieldPrefixV2) : key
  }
  return key?.startsWith(fieldPrefixV2) ? key?.replace(fieldPrefixV2, fieldPrefixV1) : key
}

export const getIsStateRequired = (selectedCountryCode: string) =>
  CountriesWithStates?.includes(selectedCountryCode)

export const getMappedAddressErrors: GetAddressErrorMethod = requestError => {
  const requestErrors = getRequestErrors(requestError)
  if (!requestErrors) return
  return Object.keys(requestErrors ?? {})?.reduce((errorsObj, requestErrorKey) => {
    const requestErrorList = requestErrors?.[requestErrorKey]
    const mappedErrorKey = Object.keys(AddressErrors)?.find(errorKey =>
      requestErrorList?.some(errorItem => errorItem?.includes(AddressErrors?.[errorKey]))
    )
    if (mappedErrorKey) {
      errorsObj[requestErrorKey] = mappedErrorKey
    }
    return errorsObj
  }, {})
}

export const setAdressFieldErrors = <FormKey extends string>(
  error: ApiErrorResponse,
  getErrorFormKey: (requestErrorKey: string) => FormKey,
  setError: UseFormSetError<FieldValues>
): boolean => {
  const addressErrors = getMappedAddressErrors(error)
  const requestErrorKeys = Object.keys(addressErrors ?? {})
  const hasMappedRequestErrors = !!requestErrorKeys?.length
  if (hasMappedRequestErrors) {
    requestErrorKeys?.forEach(requestErrorKey => {
      const formKey = getErrorFormKey(requestErrorKey)
      const messageKey = addressErrors?.[requestErrorKey]
      const message = messageKey ? i18next?.t(`addressErrors.${messageKey}`) : ''
      setError(formKey, { type: 'manual', message })
    })
  }
  return hasMappedRequestErrors
}
